import { motion } from "framer-motion";
import { Play, Sparkles } from "lucide-react";
import { Button } from "../ui/button";
import VideoHero from "./VideoHero";

export default function HeroSection() {
  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      {/* Video background */}
      <div className="absolute inset-0 z-0">
        <VideoHero />
        {/* Dark gradient overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 lg:py-40 w-full">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          {/* Overline */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
            <Sparkles className="w-4 h-4 text-blue-300" />
            <span className="text-sm text-white font-body font-medium">Producción Profesional en Madrid</span>
          </div>

          {/* Main Headline */}
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl xl:text-7xl tracking-tight text-white leading-[1.05] mb-6 drop-shadow-2xl">
            Convierte tu expertise en el
            <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"> activo de marketing </span>
            más potente
          </h1>

          {/* Subheadline */}
          <p className="font-body text-lg lg:text-xl text-slate-200 max-w-2xl mb-10 leading-relaxed drop-shadow-lg">
            Descubre cómo las empresas B2B están usando el videopodcast para generar leads cualificados,
            posicionarse como referentes y multiplicar su contenido en redes.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <a href="#solicitar-propuesta">
              <Button
                className="bg-gradient-to-r from-galvani-blue to-galvani-purple hover:opacity-90 text-white font-body font-semibold px-8 py-4 text-base rounded-full transition-all duration-300 hover:scale-105 shadow-xl shadow-blue-500/40"
                data-testid="hero-cta-primary"
              >
                Solicitar propuesta
                <Play size={16} className="ml-2" />
              </Button>
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="flex flex-wrap items-center gap-6 text-sm text-slate-200">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>Respuesta en 24h</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>Sin compromiso</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              <span>Propuesta a medida</span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 hidden md:flex flex-col items-center gap-2"
      >
        <span className="text-xs text-white/70 font-body uppercase tracking-widest">Descubre más</span>
        <div className="w-5 h-8 rounded-full border-2 border-white/40 flex items-start justify-center p-1">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
            className="w-1 h-2 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
}
